import {
  AccumulativeShadows,
  OrbitControls,
  PerspectiveCamera,
  RandomizedLight,
} from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  BrightnessContrast,
  EffectComposer,
} from '@react-three/postprocessing';
import { Perf } from 'r3f-perf';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CurrentContext from '../../utils/CurrentContext';
import { products } from '../../utils/productData';
import MeasureTool from './measureTool';

export default function Viewer3d({
  sceneData,
  currentFinishes,
  currentProduct,
  setCurrentProduct,
}) {
  const { setUuid, productsData } = useContext(CurrentContext); // Destructure uuid from useParams
  const { uuid } = useParams(); // Destructure uuid from useParams

  useEffect(() => {
    if (setUuid) {
      setUuid(uuid);
    }
  }, [uuid, setUuid]);

  // Find the component based on the uuid
  const productName = productsData?.find(
    (product) => product.id === parseInt(uuid),
  ).product;

  const getProductByName = (name) => {
    return products.find((product) => product.name === name);
  };

  // console.log(productName,"productNameproductName")
  const CurrentProduct = getProductByName(productName).component;
  const CurrentProductMeasure = productsData?.find(
    (product) => product.id === parseInt(uuid),
  );

  const [showPerformace, setShowPerformace] = useState(false);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.altKey && (event.key === 'p' || event.key === 'P')) {
        setShowPerformace((prevKeyPressed) => !prevKeyPressed);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <div>
      <div
        id="modelViewerWrapper"
        className="App"
        style={{ height: '100vh', width: '100vw' }}
      >
        {CurrentProduct ? (
          <Canvas
            shadows
            dpr={[1, 1.5]}
            id={'visibleCanvas'}
            style={{ background: '#ffffff' }}
            gl={{ preserveDrawingBuffer: true }}
          >
            {/* <color attach="background" args={['white']} /> */}

            <Cameras
              currentProduct={currentProduct}
              setCurrentProduct={setCurrentProduct}
            />

            <Lights />

            <CurrentProduct {...{ currentFinishes }} />
            <EffectComposer>
              <BrightnessContrast contrast={0.2} brightness={0.02} />
            </EffectComposer>

            <CurrentProduct {...{ currentFinishes }} />

            <MeasureTool />

            {showPerformace && <Perf position={'bottom-left'} />}
          </Canvas>
        ) : (
          <p>Product not found</p> // Fallback UI if no product matches
        )}
      </div>
    </div>
  );
}

const Cameras = ({ currentProduct, setCurrentProduct }) => {
  const { orbitCamera, autoRotate } = useContext(CurrentContext);

  // References for the cameras
  const stationaryCameraRef = useRef();
  const orbitCameraRef = useRef();

  useFrame(() => {
    if (orbitCamera === false) {
      stationaryCameraRef.current?.lookAt(0, 0, 0);
    }
  });

  const roundToTwoDecimals = (arr) =>
    arr.map((num) => parseFloat(num.toFixed(2)));

  const handleCameraChange = () => {
    const newPosition = orbitCameraRef.current.position.toArray();
    const roundedPosition = roundToTwoDecimals(newPosition);
    setCurrentProduct((prev) => ({
      ...prev,
      cameraInitPos: roundedPosition,
    }));
  };

  return (
    <>
      {/* Stationary Camera */}
      {orbitCamera === false && (
        <PerspectiveCamera
          ref={stationaryCameraRef}
          position={[
            currentProduct?.cameraInitPos[0],
            currentProduct?.cameraInitPos[1],
            currentProduct?.cameraInitPos[2],
          ]}
          fov={currentProduct?.cameraFOV}
          makeDefault
        />
      )}

      {/* Orbit Control Camera */}
      {orbitCamera === true && (
        <>
          <PerspectiveCamera
            ref={orbitCameraRef}
            position={[
              currentProduct?.cameraInitPos[0],
              currentProduct?.cameraInitPos[1],
              currentProduct?.cameraInitPos[2],
            ]}
            // fov={30}
            fov={currentProduct?.cameraFOV}
            makeDefault
          />
          <OrbitControls
            camera={orbitCameraRef.current}
            autoRotate={autoRotate}
            autoRotateSpeed={4}
            minDistance={currentProduct?.cameraMinMaxZoomDistance[0]}
            maxDistance={currentProduct?.cameraMinMaxZoomDistance[1]}
            minPolarAngle={0}
            maxPolarAngle={Math.PI / 2}
            target={[
              currentProduct?.cameraTargetPos[0],
              currentProduct?.cameraTargetPos[1],
              currentProduct?.cameraTargetPos[2],
            ]}
            onChange={handleCameraChange}
          />
        </>
      )}
    </>
  );
};

const Lights = () => {
  return (
    <>
      <ambientLight intensity={1.5} />

      <directionalLight
        position={[5, 10, 5]}
        intensity={0.4}
        shadow-mapSize-width={512}
        shadow-mapSize-height={512}
        shadow-camera-far={50}
        shadow-camera-left={-2}
        shadow-camera-right={2}
        shadow-camera-top={2}
        shadow-camera-bottom={-2}
      />

      <directionalLight
        position={[2.5, 0.25, 2.5]}
        intensity={0.6}
        shadow-mapSize-width={512}
        shadow-mapSize-height={512}
        shadow-camera-far={50}
        shadow-camera-left={-2}
        shadow-camera-right={2}
        shadow-camera-top={2}
        shadow-camera-bottom={-2}
      />

      <directionalLight
        position={[-2.5, 0.25, -2.5]}
        intensity={0.6}
        shadow-mapSize-width={512}
        shadow-mapSize-height={512}
        shadow-camera-far={50}
        shadow-camera-left={-2}
        shadow-camera-right={2}
        shadow-camera-top={2}
        shadow-camera-bottom={-2}
      />

      <directionalLight position={[-5, 10, -5]} intensity={0.5} />

      {/* <AccumulativeShadows temporal frames={100} scale={3} opacity={0.5}>
        <RandomizedLight amount={1} bias={0.01} position={[5, 10, 5]} />
      </AccumulativeShadows> */}
    </>
  );
};
